import React, {
    Children,
    cloneElement,
    useEffect,
    useRef,
} from 'react';
import { createPortal } from 'react-dom';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import { useClickOutside } from '@r1-frontend/shared/hooks/useClickOutside';
import { useIsomorphicLayoutEffect } from '@r1-frontend/shared/hooks/useIsomorphicLayoutEffect';
import { useRootElement } from '@r1-frontend/shared/hooks/useRootElement';

import { getTooltipPosition } from './helpers';
import TooltipBody from './TooltipBody';
import { isDOMTypeElement, TTooltipProps } from './types';

const WithStatelessTooltip = ({
    children,
    content,

    position = 'top-center',
    isShown,
    withArrow,

    closeOnClickOutside = true,
    closeOnTransition = false,
    closeOnScroll,
    onClose,

    background,
    gap = 4,
    arrowIndent = 16,
    radius,
    zIndex,
}: TTooltipProps) => {
    const root = useRootElement({ id: 'tooltip-root' });
    const tooltipRef = useRef<HTMLDivElement>(null);
    const child = Children.only(children);
    const childRef = useRef<HTMLElement>();
    const target = childRef.current;

    useClickOutside(
        tooltipRef,
        closeOnClickOutside && typeof onClose === 'function' && isShown
            ? onClose
            : noop,
    );

    useIsomorphicLayoutEffect(() => {
        const tooltip = tooltipRef.current;

        if (!target || !tooltip) {
            return;
        }

        const { left, top } = getTooltipPosition({
            child: target,
            tooltip,
            position,
            gap,
            arrowIndent: withArrow ? arrowIndent : 0,
        });

        tooltip.style.left = `${left}px`;
        tooltip.style.top = `${top}px`;
        tooltip.style.position = 'absolute';
    }, [position, gap, arrowIndent, isShown, withArrow, target]);

    useEffect(() => {
        const onScroll = () => {
            if (closeOnScroll && typeof onClose === 'function' && isShown) {
                onClose();
            }
        };

        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [closeOnScroll, onClose]);

    useEffect(() => {
        const onTransition = debounce(() => {
            if (closeOnTransition && typeof onClose === 'function' && isShown) {
                onClose();
            }
        }, 100);

        window.addEventListener('transitionstart', onTransition);
        return () => window.removeEventListener('transitionstart', onTransition);
    }, [closeOnTransition, onClose]);

    const setRef = (element: HTMLElement) => {
        childRef.current = element;

        const ref = isDOMTypeElement<React.HTMLAttributes<HTMLElement>, HTMLElement>(child)
            ? child.ref
            : null;

        if (!ref) {
            return;
        }

        if (typeof ref === 'function') {
            return ref(element);
        }

        (ref as React.MutableRefObject<HTMLElement>).current = element;
    };

    return (
        <>
            {cloneElement(child, { ref: setRef })}
            {root &&
                isShown &&
                target &&
                createPortal(
                    <TooltipBody
                        ref={tooltipRef}
                        content={content}
                        position={position}
                        background={background}
                        arrowIndent={arrowIndent}
                        withArrow={withArrow}
                        radius={radius}
                        zIndex={zIndex}
                    />,
                    root,
                )}
        </>
    );
};

export default WithStatelessTooltip;
